import { FieldValues } from "react-hook-form";
import { supabase } from "../supabaseClient";
import { MEDIA_SIZE, getMediaPathWithFilename } from "./files";
import { setup_notifications } from "./setup_notification";
import { Feeds } from "../types/api/Feed";
import { SelectedFile } from "../types/PostSelection";
import { getSelectedMediaIds } from "./helper/getSelectedMediaIds";
import { hasCompleteImageDerivatives } from "../shared/imageDerivatives";
import {
  getSelectedFileKey,
  resolvePostMediaSelections,
} from "./helper/resolvePostMediaSelections";
import { Database } from "../types/supabase";

type PostMediaInsert = Database["public"]["Tables"]["posts_media"]["Insert"];

export async function schedule(
  feeds: Feeds,
  values: FieldValues,
  selected: SelectedFile[]
) {
  const { allFiles: imgs, feedSelections } = resolvePostMediaSelections(
    feeds,
    values,
    selected
  );

  for (const img of imgs) {
    if (
      !img.name.endsWith(".video-thumbnail.jpg") &&
      !hasCompleteImageDerivatives(img.imageDerivatives)
    ) {
      throw new Error(`Image derivatives are unavailable for ${img.name}`);
    }
  }

  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session) {
    throw new Error("User not authenticated");
  }

  const mediaWithoutPost: PostMediaInsert[] = [];

  for (let index = 0; index < imgs.length; index++) {
    const img = imgs[index];
    const isVideo = img.name.endsWith(".video-thumbnail.jpg");
    let filename = img.name;
    let mimetype = img.mine_type;

    if (isVideo) {
      filename = img.name.replace(".video-thumbnail.jpg", "");
      const { data: videoDetails } = await supabase.storage
        .from("content")
        .list(`${session.user.id}/media/video`, { search: filename });

      console.log("VIDEO", filename, videoDetails);

      if (!videoDetails?.[0]?.metadata?.mimetype) {
        throw new Error(`Video metadata is unavailable for ${filename}`);
      }
      mimetype = videoDetails[0].metadata.mimetype;
    }

    const imageDerivatives = img.imageDerivatives;
    if (!isVideo) {
      mimetype = imageDerivatives!.derivatives["3600px"]!.mediaType;
    }

    mediaWithoutPost.push({
      title: img.name,
      alt_text: img.altText || null,
      src_thumbnail: isVideo
        ? await getMediaPathWithFilename(MEDIA_SIZE.thumbnail, img.name)
        : imageDerivatives!.derivatives.thumbnail!.path,
      src_960: isVideo
        ? await getMediaPathWithFilename(MEDIA_SIZE.video, filename)
        : imageDerivatives!.derivatives["960px"]!.path,
      src_2400: isVideo
        ? await getMediaPathWithFilename(MEDIA_SIZE.video, filename)
        : imageDerivatives!.derivatives["2400px"]!.path,
      src_3600: isVideo
        ? await getMediaPathWithFilename(MEDIA_SIZE.video, filename)
        : imageDerivatives!.derivatives["3600px"]!.path,
      src_original: isVideo
        ? await getMediaPathWithFilename(MEDIA_SIZE.video, filename)
        : imageDerivatives!.originalPath,
      mine_type: mimetype,
      user_id: session.user.id,
      order: index,
    });
  }

  const { data: post, error: postError } = await supabase
    .from("posts")
    .insert({
      type: values.default.type,
      publish_at: new Date(values.date).toUTCString(),
      disable_auto_publish: !!values.disable_auto_publish,
      user_id: session.user.id,
    })
    .select()
    .limit(1)
    .single();

  if (postError) throw postError;

  const media = mediaWithoutPost.map((item) => ({
    ...item,
    post_id: post.id,
    post: post.id,
  }));
  const { data: postMedia, error: postMediaError } = await supabase
    .from("posts_media")
    .insert(media)
    .select("id, src_original");

  if (postMediaError) throw postMediaError;

  const enhancedImgs =
    postMedia
      ?.map((pm) => {
        const inputIndex = mediaWithoutPost.findIndex(
          (item) => item.src_original === pm.src_original
        );
        if (inputIndex === -1) return null;

        return {
          postMediaId: pm.id,
          selectionKey: getSelectedFileKey(imgs[inputIndex]),
        };
      })
      .filter(
        (
          item
        ): item is {
          postMediaId: number;
          selectionKey: string;
        } => !!item
      ) ?? [];

  const postsForFeeds = feedSelections.map(({ feed, files }) => {
    const feedValues = values[feed.slug!];

    return {
      post_id: post.id,
      post: post.id,
      feed_id: feed.id,
      feed: feed.id,
      content: feedValues.post_content,
      auto_publish: !!feedValues.auto_publish,
      type: feedValues.type,
      location: feedValues.location,
      hashtags: feedValues.hashtags,
      positions: feedValues.positions || {},
      selected_saved_descriptions:
        feedValues.selectedSavedDescriptions || [],
      user_id: session.user.id,
      selectedFiles: getSelectedMediaIds(enhancedImgs, files),
    };
  });

  const { error: feedsError } = await supabase
    .from("posts_for_feeds")
    .insert(postsForFeeds);
  if (feedsError) throw feedsError;

  await setup_notifications("create", post);

  return {
    error: null,
  };
}
